/**
 * QuestionsAndFindingsSection — Merged Q&A + Findings display
 *
 * Renders each question with its answer, followed by findings linked to that question.
 * General findings (not linked to any question) appear at the bottom.
 * Includes findings generation header/controls.
 */

import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  HelpCircle,
  ShieldCheck,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { useFindingsByChecklistItem } from "@/packages/features-shared";
import type { UIChecklistItem, Id } from "@/packages/types";
import { QuestionCard } from "./QuestionCard";
import { FindingsGenerationStatus } from "./findings/FindingsGenerationStatus";
import { DraftFindingCard } from "./findings/DraftFindingCard";
import { DraftBulkActions } from "./findings/DraftBulkActions";
import { FindingCard } from "./findings/FindingCard";
import { ManualFindingForm } from "./findings/ManualFindingForm";
import {
  ChecklistGenerationWorkflowCard,
  ChecklistSummaryPreviewCard,
  shouldShowChecklistWorkflowCard,
} from "./ChecklistGenerationWorkflowCard";
import { useTranslation } from "@/packages/utils";
import { toast } from "sonner";

// ─── Props ──────────────────────────────────────────────────────────────────

interface QuestionsAndFindingsSectionProps {
  item: UIChecklistItem;
  checklistItemId: Id<"checklistItems">;
  onSwitchToEvidence: () => void;
  onGenerateEverything?: () => void;
  onOpenSummaryTab?: () => void;
}

// ─── Inline Findings List for a specific filter ─────────────────────────────

type FindingDoc = NonNullable<
  ReturnType<typeof useFindingsByChecklistItem>
>[number];

interface InlineFindingsProps {
  findings: FindingDoc[];
  editingFindingId: string | null;
  onEdit: (id: string) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
}

function InlineFindings({
  findings,
  editingFindingId,
  onEdit,
  onEditSave,
  onEditCancel,
}: InlineFindingsProps) {
  if (findings.length === 0) return null;

  const drafts = findings.filter((f) => f.status === "draft");
  const confirmed = findings.filter((f) => f.status !== "draft");

  return (
    <div className="space-y-2 ml-3 pl-3 border-l-2 border-border/50">
      {drafts.map((finding) => (
        <DraftFindingCard
          key={finding._id}
          finding={finding}
          isEditing={editingFindingId === finding._id}
          onEdit={() => onEdit(finding._id)}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      ))}
      {confirmed.map((finding) => (
        <FindingCard
          key={finding._id}
          finding={finding}
          isEditing={editingFindingId === finding._id}
          onEdit={() => onEdit(finding._id)}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      ))}
    </div>
  );
}

// ─── Batch Progress Banner ──────────────────────────────────────────────────

function BatchProgressBanner({
  generated,
  total,
  done,
}: {
  generated: number;
  total: number;
  done: boolean;
}) {
  const pct = total > 0 ? Math.round((generated / total) * 100) : 0;
  const { t } = useTranslation("audits");

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2.5">
      <div className="flex items-center gap-2">
        {done ? (
          <CheckCircle2 size={14} className="text-green-500 shrink-0" />
        ) : (
          <Loader2 size={14} className="text-accent shrink-0 animate-spin" />
        )}
        <span className="text-[12px] text-secondary flex-1">
          {done
            ? t("checklist.batchProgress.done")
            : t("checklist.batchProgress.generating", { generated, total })}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-accent/10 overflow-hidden">
        <div
          className="h-full rounded-full bg-accent transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── No Findings Success Card ────────────────────────────────────────────────

function NoFindingsSuccessCard({ visible }: { visible: boolean }) {
  const { t } = useTranslation("audits");
  return (
    <div
      className={`overflow-hidden transition-all duration-500 ${visible ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}
    >
      <div className="rounded-lg border border-green-500/20 bg-green-500/5 px-4 py-3.5 flex items-center gap-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-500/10">
          <ShieldCheck size={18} className="text-green-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-green-700 dark:text-green-400">
            {t("checklist.noFindingsSuccess.title")}
          </p>
          <p className="text-[11px] text-green-600/70 dark:text-green-400/70">
            {t("checklist.noFindingsSuccess.description")}
          </p>
        </div>
      </div>
    </div>
  );
}

function StaleFindingsBanner(_: { checklistItemId: Id<"checklistItems"> }) {
  const { t } = useTranslation("audits");

  return (
    <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/30">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <p className="flex-1 text-[12px] text-amber-800 dark:text-amber-300">
        {t(
          "findings.section.staleWarning",
          "Answers have changed since findings were generated",
        )}
      </p>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function QuestionsAndFindingsSection({
  item,
  checklistItemId,
  onSwitchToEvidence,
  onGenerateEverything,
  onOpenSummaryTab,
}: QuestionsAndFindingsSectionProps) {
  const { t } = useTranslation("audits");
  const findings = useFindingsByChecklistItem(checklistItemId);
  const [editingFindingId, setEditingFindingId] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{
    total: number;
    baselineAnswered: number;
  } | null>(null);
  const batchDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const batchTimeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const questions = item.questions ?? [];
  const answers = item.answers ?? [];

  const isGenerating = item.findingsGenerationStatus === "generating";
  const hasFailed = item.findingsGenerationStatus === "failed";
  const isCompleted = item.findingsGenerationStatus === "completed";

  // Track "no findings" success card
  const [showNoFindings, setShowNoFindings] = useState(false);
  const prevStatusRef = useRef(item.findingsGenerationStatus);

  useEffect(() => {
    if (item.findingsGenerationStatus === "generating") {
      setShowNoFindings(false);
    }

    const prev = prevStatusRef.current;
    prevStatusRef.current = item.findingsGenerationStatus;

    if (prev === "generating" && isCompleted) {
      if ((findings ?? []).length === 0) {
        setShowNoFindings(true);
      } else {
        setShowNoFindings(false);
        toast.success(
          t(
            "findings.section.autoGenerated",
            "Findings generated automatically",
          ),
        );
      }
    }
  }, [findings, isCompleted, item.findingsGenerationStatus, t]);

  const allFindings = findings ?? [];
  const drafts = allFindings.filter((f) => f.status === "draft");
  const sortedQuestions = [...questions].sort((a, b) => a.order - b.order);
  const answerMap = new Map(answers.map((a) => [a.question.order, a]));
  const unansweredCount = questions.filter(
    (q) => !answerMap.has(q.order),
  ).length;

  // Batch progress computation
  const batchGenerated = batchProgress
    ? Math.min(
        answers.length - batchProgress.baselineAnswered,
        batchProgress.total,
      )
    : 0;
  const batchDone = batchProgress
    ? batchGenerated >= batchProgress.total
    : false;

  // Auto-dismiss when batch completes
  useEffect(() => {
    if (batchDone && batchProgress) {
      batchDismissTimer.current = setTimeout(() => {
        setBatchProgress(null);
      }, 1500);
      if (batchTimeoutTimer.current) {
        clearTimeout(batchTimeoutTimer.current);
        batchTimeoutTimer.current = null;
      }
    }
    return () => {
      if (batchDismissTimer.current) {
        clearTimeout(batchDismissTimer.current);
      }
    };
  }, [batchDone, batchProgress]);

  // Timeout: auto-dismiss after 60s to avoid stuck banner
  useEffect(() => {
    if (batchProgress && !batchDone) {
      batchTimeoutTimer.current = setTimeout(() => {
        setBatchProgress(null);
      }, 60_000);
    }
    return () => {
      if (batchTimeoutTimer.current) {
        clearTimeout(batchTimeoutTimer.current);
      }
    };
  }, [batchProgress, batchDone]);

  function handleEdit(findingId: string) {
    setEditingFindingId(findingId);
  }

  function handleEditSave() {
    setEditingFindingId(null);
  }

  function handleEditCancel() {
    setEditingFindingId(null);
  }

  function getFindingsForQuestion(order: number) {
    return allFindings.filter((f) => f.questionOrder === order);
  }

  const generalFindings = allFindings.filter(
    (f) => f.questionOrder === undefined || f.questionOrder === null,
  );

  const showAiBanner =
    !!batchProgress ||
    unansweredCount > 1 ||
    isGenerating ||
    isCompleted ||
    hasFailed;

  const isFullPipelineRunning = item.fullPipelineStatus === "running";
  const showWorkflowCard = shouldShowChecklistWorkflowCard(item, {
    allowIdlePrompt: true,
    canGenerateEverything: !!onGenerateEverything,
    isBusy: isGenerating || !!batchProgress,
  });
  const showSummaryPreview =
    !!item.summaryReport ||
    item.summaryStatus === "pending" ||
    item.summaryStatus === "failed";

  return (
    <div className="space-y-4 pb-4">
      {(showWorkflowCard || showSummaryPreview) && (
        <div className="w-full space-y-3">
          {showWorkflowCard && (
            <ChecklistGenerationWorkflowCard
              item={item}
              onGenerateEverything={onGenerateEverything}
            />
          )}
          {showSummaryPreview && (
            <ChecklistSummaryPreviewCard
              item={item}
              onOpenSummaryTab={onOpenSummaryTab}
            />
          )}
        </div>
      )}

      {/* AI actions banner / batch progress */}
      {batchProgress ? (
        <BatchProgressBanner
          generated={batchGenerated}
          total={batchProgress.total}
          done={batchDone}
        />
      ) : (
        !isFullPipelineRunning &&
        showAiBanner && (
          <>
            {/* <div className="flex items-center gap-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2"> */}
            {/* <Sparkles size={14} className="text-accent shrink-0" />
            <span className="text-[12px] text-secondary flex-1">
              {unansweredCount > 1
                ? t("checklist.batchGenerate.unanswered", {
                    count: unansweredCount,
                  })
                : t("findings.section.title")}
            </span> */}
            {/* {unansweredCount > 1 && (
              <BatchGenerateButton
                checklistItemId={item.id}
                unansweredCount={unansweredCount}
                onStarted={handleBatchStarted}
                disabled={!!batchProgress}
              />
            )}
          </div> */}
          </>
        )
      )}

      {isGenerating && !isFullPipelineRunning && (
        <FindingsGenerationStatus state="loading" />
      )}
      {hasFailed && !isGenerating && !isFullPipelineRunning && (
        <FindingsGenerationStatus
          state="error"
          checklistItemId={checklistItemId}
        />
      )}

      {item.findingsStale && !isGenerating && !isFullPipelineRunning && (
        <StaleFindingsBanner checklistItemId={checklistItemId} />
      )}

      {/* No findings success card */}
      <NoFindingsSuccessCard visible={showNoFindings} />

      {/* Draft bulk actions (applies to all drafts) */}
      {!isGenerating && drafts.length > 0 && (
        <DraftBulkActions
          checklistItemId={checklistItemId}
          draftCount={drafts.length}
        />
      )}

      {/* Questions with their associated findings */}
      {sortedQuestions.length > 0 ? (
        <div className="space-y-3">
          {sortedQuestions.map((question) => {
            const questionFindings = getFindingsForQuestion(question.order);

            return (
              <div key={question.order} className="space-y-2">
                <QuestionCard
                  question={question}
                  existingAnswer={answerMap.get(question.order)}
                  checklistItemId={item.id}
                  onSwitchToEvidence={onSwitchToEvidence}
                  item={item}
                  isBatchGenerating={
                    !!batchProgress &&
                    !batchDone &&
                    !answerMap.has(question.order)
                  }
                />

                {/* Findings for this question */}
                {!isGenerating && (
                  <InlineFindings
                    findings={questionFindings}
                    editingFindingId={editingFindingId}
                    onEdit={handleEdit}
                    onEditSave={handleEditSave}
                    onEditCancel={handleEditCancel}
                  />
                )}

                {/* Add finding button per question */}
                {!isGenerating && (
                  <div className="ml-3 pl-3 border-l-2 border-border/50">
                    <ManualFindingForm
                      checklistItemId={checklistItemId}
                      questionOrder={question.order}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-4 gap-2">
          <HelpCircle size={24} className="text-muted/50" />
          <p className="text-[12px] text-muted">
            {t("checklist.questions.noQuestionsYet")}
          </p>
        </div>
      )}

      {/* General findings (not tied to any question) */}
      {!isGenerating && generalFindings.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-border/40">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-medium text-muted uppercase tracking-wider">
                {t("findings.section.generalFindingsTitle")}
              </h4>
              <p className="text-[11px] text-muted mt-0.5">
                {t("findings.section.generalFindingsDescription")}
              </p>
            </div>
            <ManualFindingForm checklistItemId={checklistItemId} />
          </div>

          <InlineFindings
            findings={generalFindings}
            editingFindingId={editingFindingId}
            onEdit={handleEdit}
            onEditSave={handleEditSave}
            onEditCancel={handleEditCancel}
          />
        </div>
      )}
    </div>
  );
}
